import axios from 'axios';

import { ApiResponse } from '../../models/api.response.model';
import { Error } from '../../models/error.model';
import { CompanyAccountingAccounts } from '../../models/company.accounting.accounts.model';
import { CompanyAccountingTreeAccounts } from '../../models/company.accounting.tree.accounts.model';
import { CompanyAccountingAvailableAccounts } from '../../models/company.accounting.available.accounts.model';
import { AccountingAccount } from '../../models/accounting.account.model';
import { Company } from '../../models/company.model';
import { authToken } from '../../scripts/authentication.script';

export class AccountingAccountDA {
  static apiResponse: ApiResponse

  public static async getAccountingAccounts(
    id_company: string,
    include: string[],
    from: string[],
    between: string[][]
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyAccountingAccounts: CompanyAccountingAccounts | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/accounting.accounts.data.json').then(response => { companyAccountingAccounts = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyAccountingAccounts!.id_company === id_company) {
        let regex: RegExp = new RegExp('')

        if (include.length > 0 && from.length === 0 && between.length === 0) {
          regex = new RegExp(`^(${include.join('|')})(\\.\\d+)?$`)
        }
        if (include.length === 0 && from.length > 0 && between.length === 0) {
          regex = new RegExp(`^(${from.map(code => `${code[0]}\\d{3}`).join('|')})(\\.\\d+)?$`)
        }
        if (include.length === 0 && from.length === 0 && between.length > 0) {
          regex = new RegExp(`^(${between.map(([start, end]) => {
            if (start === end) return start
            return Array.from({length: parseInt(end, 10) - parseInt(start, 10) + 1}, (_, i) => parseInt(start, 10) + i).join('|')
          }).join('|')})(\\.\\d+)?$`)
        }

        companyAccountingAccounts!.filter = { include: include, from: from, between: between }
        companyAccountingAccounts!.accounting_accounts = companyAccountingAccounts!.accounting_accounts.filter(item => regex.test(item.code))

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: companyAccountingAccounts
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      const betweenStr = between
        .filter(pair => pair.length === 2 && pair[0] && pair[1])
        .map(pair => `${pair[0]}:${pair[1]}`)
        .join(',')
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/accounts?codes=${include}&from=${from}&between=${betweenStr}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        const raw = response.data?.data ?? response.data
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: {
            id_company,
            filter: { include, from, between: [] },
            accounting_accounts: Array.isArray(raw) ? raw : []
          }
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getAccountingTreeAccounts(
    id_company: string,
    company_language: string,
    accounting_account_code_name: string,
    sort_field: string,
    sort_mode: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyAccountingTreeAccounts: CompanyAccountingTreeAccounts | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/accounting.accounts.tree.data.json').then(response => { companyAccountingTreeAccounts = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyAccountingTreeAccounts!.id_company === id_company) {
        companyAccountingTreeAccounts!.filter = { code_name: accounting_account_code_name, sort_field: sort_field, sort_mode: sort_mode }

        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: companyAccountingTreeAccounts
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/accounts/tree?language=${company_language}&code_name=${accounting_account_code_name}&sort_field=${sort_field}&sort_mode=${sort_mode}`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        const raw = response.data?.data ?? response.data
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: {
            id_company,
            filter: raw?.filter ?? { code_name: accounting_account_code_name, sort_field, sort_mode },
            accounting_accounts: raw?.accounting_accounts ?? (Array.isArray(raw) ? raw : [])
          }
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getAccountingAvailableAccounts(
    id_company: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let companyAccountingAvailableAccounts: CompanyAccountingAvailableAccounts | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/accounting.accounts.available.data.json').then(response => { companyAccountingAvailableAccounts = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (companyAccountingAvailableAccounts!.id_company === id_company) {
        this.apiResponse = {
          status: 200,
          message: 'Success',
          data: companyAccountingAvailableAccounts
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/accounts/available`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      await axios.get(url, config).then(response => {
        const raw = response.data?.data ?? response.data
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: {
            id_company,
            accounting_accounts: {
              cash: (raw?.cash ?? []).map((a: any) => ({ ...a, id: a.id ?? a._id ?? '', available: a.available !== false })),
              postal: (raw?.postal ?? []).map((a: any) => ({ ...a, id: a.id ?? a._id ?? '', available: a.available !== false })),
              bank: (raw?.bank ?? []).map((a: any) => ({ ...a, id: a.id ?? a._id ?? '', available: a.available !== false })),
              current_account: (raw?.current_account ?? []).map((a: any) => ({ ...a, id: a.id ?? a._id ?? '', available: a.available !== false })),
              shareholder: (raw?.shareholder ?? raw?.current_account ?? []).map((a: any) => ({ ...a, id: a.id ?? a._id ?? '', available: a.available !== false })),
            }
          }
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getTaxCodes(id_company: string): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/tax-codes`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.get(url, config).then(response => {
      const raw = response.data?.data ?? response.data
      const codes = Array.isArray(raw) ? raw : []

      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: codes
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async getNextCode(id_company: string, id_parent: string): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/accounts/${id_parent}/next-code`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.get(url, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async postAccountingAccount(
    id_company: string,
    id_parent: string,
    type: string,
    code: string,
    name: { en: string, fr: string, it: string, de: string },
    currency: string,
    id_sibling?: string
  ): Promise<ApiResponse> {
    if (process.env.REACT_APP_ENVIRONMENT === 'development') {
      let company: Company | null = null
      let accountingAccount: AccountingAccount | null = null
      let error: Error | null = null

      await axios.get('../../../../../database/company.data.json').then(response => { company = response.data }).catch(error => { alert(error) })
      await axios.get('../../../../../database/error.data.json').then(response => { error = response.data }).catch(error => { alert(error) })

      if (company!.id === id_company) {
        accountingAccount = {
          id: `account${(Math.floor(Math.random() * 1000)).toString()}`,
          code: code,
          type: type,
          level: 3,
          coefficient: 0,
          available: true,
          id_parent: id_parent,
          id_mirror_account: '',
          name: name,
          currency: company!.currency,
          children: []
        }

        this.apiResponse = {
          status: 201,
          message: 'Success',
          data: accountingAccount
        }
      } else {
        this.apiResponse = {
          status: 400,
          message: 'Bad request',
          data: error
        }
      }
    } else {
      let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/accounts`

      let config: any = {
        headers: { Authorization: authToken() }
      }

      let data: any = {
        id_parent: id_parent,
        type: type,
        code: code,
        name: name,
        currency: currency,
        ...(id_sibling && { id_sibling }),
      }

      await axios.post(url, data, config).then(response => {
        this.apiResponse = {
          status: response.status,
          message: response.statusText,
          data: response.data?.data ?? response.data
        }
      }).catch(error => {
        this.apiResponse = {
          status: error.response?.status ?? 500,
          message: error.response?.statusText ?? 'Error',
          data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
        }
      })
    }

    return this.apiResponse
  }

  public static async getFiscalPeriods(id_company: string): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/fiscal-periods`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.get(url, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async getEntries(id_company: string, params?: Record<string, string>): Promise<ApiResponse> {
    const query = new URLSearchParams(params || {}).toString();
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/entries${query ? '?' + query : ''}`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.get(url, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async createFiscalPeriod(id_company: string, data: {
    tax_period: string,
    tax_statement: string,
    regime: string,
    method: string
  }): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/fiscal-periods`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.post(url, data, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async getVatRegime(id_company: string): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/vat-regime`
    let config: any = { headers: { Authorization: authToken() } }

    await axios.get(url, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async putVatRegime(id_company: string, subject_tax: boolean, tax_method: string, tax_rate: string, tax_declaration: string, declaration_periodicity: string = ''): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/vat-regime`
    let config: any = { headers: { Authorization: authToken() } }
    let data: any = { subject_tax: !!subject_tax, tax_method, tax_rate: String(tax_rate), tax_declaration, declaration_periodicity }

    await axios.put(url, data, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }

  public static async putAccountingAccountName(
    id_company: string,
    id_account: string,
    name: { fr?: string, en?: string, de?: string, it?: string }
  ): Promise<ApiResponse> {
    let url: string = `${process.env.REACT_APP_PORT_ADMIN}/${id_company}/accounting/accounts/${id_account}/name`

    let config: any = {
      headers: { Authorization: authToken() }
    }

    await axios.put(url, name, config).then(response => {
      this.apiResponse = {
        status: response.status,
        message: response.statusText,
        data: response.data?.data ?? response.data
      }
    }).catch(error => {
      this.apiResponse = {
        status: error.response?.status ?? 500,
        message: error.response?.statusText ?? 'Error',
        data: error.response?.data?.error ?? { code: 'NETWORK_ERROR', message: error.message }
      }
    })

    return this.apiResponse
  }
}
